import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActionArea,
    Chip,
} from '@mui/material';
import PageHeader from '../components/common/PageHeader';
import {
    QuestionAnswer as FaqIcon,
    FolderOpen as LibraryIcon,
    Code as ApiIcon,
    ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../store/authStore';
import { AccountType } from '../types/auth';

interface KbModuleCard {
    title: string;
    description: string;
    icon: React.ReactElement;
    path: string;
    color: string;
    gradient: string;
    badge?: string;
    requiredRole?: AccountType;
}

const modules: KbModuleCard[] = [
    {
        title: 'Document Library',
        description:
            'Browse and download curated documents, guides, and resources organized into folders. Upload and manage files for your team.',
        icon: <LibraryIcon sx={{ fontSize: 32 }} />,
        path: '/knowledge-base/library',
        color: '#90bf45',
        gradient: 'linear-gradient(135deg, #90bf45 0%, #557010 100%)',
        badge: 'Files & Folders',
    },
    {
        title: 'FAQ',
        description:
            'Find quick answers to frequently asked questions, organized by category. A searchable, always-up-to-date knowledge repository.',
        icon: <FaqIcon sx={{ fontSize: 32 }} />,
        path: '/knowledge-base/faq',
        color: '#17a2b8',
        gradient: 'linear-gradient(135deg, #17a2b8 0%, #117a8b 100%)',
        badge: 'Quick Answers',
    },
    {
        title: 'API Documentation',
        description:
            'Explore CarbonCare integration API scenarios with a live payload explorer. Test REST and XML/SOAP endpoints interactively.',
        icon: <ApiIcon sx={{ fontSize: 32 }} />,
        path: '/knowledge-base/api-docs',
        color: '#56686C',
        gradient: 'linear-gradient(135deg, #56686C 0%, #373e3f 100%)',
        badge: '90+ Scenarios',
        requiredRole: AccountType.User,
    },
];

const KnowledgeBasePage: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuthStore();

    const visibleModules = modules.filter((m) => {
        if (!m.requiredRole) return true;
        return user && user.accountType >= m.requiredRole;
    });

    return (
        <Box>
            {/* Page Header */}
            <PageHeader description="Find guides, FAQs, and API documentation in one place." />

            {/* Module Cards */}
            <Grid container spacing={3}>
                {visibleModules.map((mod) => (
                    <Grid item xs={12} md={4} key={mod.path}>
                        <Card
                            elevation={0}
                            sx={{
                                height: '100%',
                                border: '1px solid rgba(0,0,0,0.08)',
                                borderRadius: 3,
                                background: '#fff',
                                transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    height: '3px',
                                    background: mod.gradient,
                                    opacity: 0,
                                    transition: 'opacity 0.25s ease',
                                },
                                '&:hover': {
                                    transform: 'translateY(-5px)',
                                    boxShadow: '0 12px 32px rgba(0,0,0,0.10)',
                                    border: `1px solid ${mod.color}30`,
                                    '&::before': { opacity: 1 },
                                },
                            }}
                        >
                            <CardActionArea
                                onClick={() => navigate(mod.path)}
                                sx={{ height: '100%', p: 0, '&:hover': { background: 'transparent' } }}
                            >
                                <CardContent sx={{ p: 3.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                    {/* Icon */}
                                    <Box
                                        sx={{
                                            width: 60,
                                            height: 60,
                                            borderRadius: 2.5,
                                            background: mod.gradient,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#fff',
                                            mb: 2.5,
                                            boxShadow: `0 6px 16px ${mod.color}35`,
                                        }}
                                    >
                                        {mod.icon}
                                    </Box>

                                    {/* Badge */}
                                    {mod.badge && (
                                        <Chip
                                            label={mod.badge}
                                            size="small"
                                            sx={{
                                                mb: 1.5,
                                                alignSelf: 'flex-start',
                                                height: 22,
                                                fontSize: '0.7rem',
                                                fontWeight: 600,
                                                letterSpacing: '0.04em',
                                                bgcolor: `${mod.color}14`,
                                                color: mod.color,
                                                border: `1px solid ${mod.color}30`,
                                            }}
                                        />
                                    )}

                                    {/* Title */}
                                    <Typography variant="h6" fontWeight={700} sx={{ color: '#1f2937', mb: 1, lineHeight: 1.3 }}>
                                        {mod.title}
                                    </Typography>

                                    {/* Description */}
                                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, flexGrow: 1 }}>
                                        {mod.description}
                                    </Typography>

                                    {/* CTA Row */}
                                    <Box
                                        sx={{
                                            mt: 3,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.5,
                                            color: mod.color,
                                            fontWeight: 600,
                                            fontSize: '0.85rem',
                                        }}
                                    >
                                        <Typography variant="body2" fontWeight={600} sx={{ color: mod.color }}>
                                            Open module
                                        </Typography>
                                        <ArrowIcon sx={{ fontSize: 16 }} />
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};

export default KnowledgeBasePage;
